import React, { useState, useEffect, useMemo, useRef } from 'react';
import { ArrowLeft, Save, Loader, Upload, X } from 'lucide-react';
import { useLocation } from 'react-router-dom';

import VideoPreview from '../components/VideoPreview';
import { Screen, LayoutType } from '../../../shared/types';
import { useStore } from '../../../store/useStore';
import { bannerSyncService, TIPO_TO_LAYOUT, LAYOUT_TO_TIPO, parseConfigForPatch } from '../../banners/services/bannerSyncService';
import { bannerService, AlterarTelaBannerCommand } from '../../banners/services/bannerService';
import { useBannersParaVideo } from '../../banners/hooks/useBanners';

interface LocationState {
  screenId?: number;
}

const VideoUploadPage: React.FC = () => {
  const location = useLocation();
  const { screens: screensStore } = useStore();
  // Usar hook otimizado específico para página de vídeos
  // Retorna: id, nome, tipo, ativo, video (flag indicando se tem vídeo)
  const { telas: telasOtimizadas, loading: loadingTelasAPI, error: errorTelasAPI } = useBannersParaVideo();
  
  const [selectedScreenId, setSelectedScreenId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [loadingScreen, setLoadingScreen] = useState(false);
  const [screenFromAPI, setScreenFromAPI] = useState<Screen | null>(null);
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [loop, setLoop] = useState(false);
  const [uploading, setUploading] = useState(false);
  const videoUrlRef = useRef<string | null>(null);

  // Filtrar apenas telas do tipo "video" (tipo 2) e converter para formato Screen
  const videoScreens = useMemo(() => {
    if (!telasOtimizadas || telasOtimizadas.length === 0) {
      return [];
    }

    return telasOtimizadas
      .filter(tela => tela.tipo === 2) // Apenas telas do tipo "video"
      .map(telaOtimizada => {
        const layoutId = 'video' as LayoutType;
        const screenStore = screensStore.find(s => s.id === telaOtimizada.id);

        return {
          id: telaOtimizada.id,
          name: telaOtimizada.nome,
          description: screenStore?.description || '',
          layoutId: layoutId,
          promotions: screenStore?.promotions || [],
          isActive: telaOtimizada.ativo ?? screenStore?.isActive ?? false,
          backgroundColor: screenStore?.backgroundColor || '#ffffff',
          backgroundImage: screenStore?.backgroundImage,
          showHeader: screenStore?.showHeader ?? true,
          duration: screenStore?.duration || 30,
          layoutStyle: screenStore?.layoutStyle,
          createdAt: screenStore?.createdAt || new Date().toISOString(),
          linkedProducts: screenStore?.linkedProducts
        } as Screen;
      });
  }, [telasOtimizadas, screensStore]);

  // Usar telas filtradas
  const screens = videoScreens.length > 0 ? videoScreens : screensStore.filter(s => s.layoutId === 'video');

  useEffect(() => {
    const state = location.state as LocationState;
    if (state && state.screenId) {
      setSelectedScreenId(state.screenId);
    }
  }, [location]);

  // Carregar dados da tela selecionada e vídeo
  useEffect(() => {
    const loadScreenFromAPI = async () => {
      if (!selectedScreenId || selectedScreenId <= 0) {
        setScreenFromAPI(null);
        setVideoUrl(null);
        return;
      }

      setLoadingScreen(true);
      try {
        const telasAPI = await bannerService.listarTelasOtimizado(selectedScreenId);
        const telaAPI = telasAPI.find(t => t.id === selectedScreenId);
        
        if (telaAPI) {
          const layoutId = 'video' as LayoutType;
          
          const localScreenData = screens.find(s => s.id === selectedScreenId);
          
          const combinedScreen: Screen = {
            id: selectedScreenId,
            name: telaAPI.nome || localScreenData?.name || 'Tela sem nome',
            description: telaAPI.descricao || localScreenData?.description || '',
            layoutId: layoutId,
            promotions: telaAPI.produtoIds || [],
            linkedProducts: localScreenData?.linkedProducts || [],
            isActive: telaAPI.ativo ?? localScreenData?.isActive ?? false,
            createdAt: localScreenData?.createdAt || new Date().toISOString(),
            backgroundColor: localScreenData?.backgroundColor || '#ffffff',
            backgroundImage: localScreenData?.backgroundImage,
            showHeader: localScreenData?.showHeader ?? true,
            duration: localScreenData?.duration || 30,
            layoutStyle: localScreenData?.layoutStyle
          };
          
          setScreenFromAPI(combinedScreen);
          
          // IMPORTANTE: Carregar vídeo apenas se a tela tiver flag video === true
          // Verificar flag video da tela otimizada
          const telaOtimizada = telasOtimizadas.find(t => t.id === selectedScreenId);
          const temVideo = telaOtimizada?.video === true;
          
          if (temVideo) {
            // Tela tem vídeo - carregar vídeo atual
            try {
              const videoBlob = await bannerService.downloadVideo(selectedScreenId);
              if (videoBlob && videoBlob.size > 0) {
                // Limpar blob URL anterior se existir
                if (videoUrlRef.current && videoUrlRef.current.startsWith('blob:')) {
                  URL.revokeObjectURL(videoUrlRef.current);
                }
                
                const blobUrl = URL.createObjectURL(videoBlob);
                videoUrlRef.current = blobUrl;
                setVideoUrl(blobUrl);
                
                // Extrair loop das configurações se existir
                if (telaAPI.configuracoes) {
                  try {
                    const config = JSON.parse(telaAPI.configuracoes);
                    if (config.loop !== undefined) {
                      setLoop(config.loop);
                    }
                  } catch (error) {
                    console.warn('Erro ao parsear configurações para loop:', error);
                  }
                }
              } else {
                // Limpar blob URL anterior se existir
                if (videoUrlRef.current && videoUrlRef.current.startsWith('blob:')) {
                  URL.revokeObjectURL(videoUrlRef.current);
                  videoUrlRef.current = null;
                }
                setVideoUrl(null);
              }
            } catch (error) {
              // Vídeo não existe ou erro ao baixar - não é crítico
              // Erro ao baixar vídeo
              // Limpar blob URL anterior se existir
              if (videoUrlRef.current && videoUrlRef.current.startsWith('blob:')) {
                URL.revokeObjectURL(videoUrlRef.current);
                videoUrlRef.current = null;
              }
              setVideoUrl(null);
            }
          } else {
            // Tela não tem vídeo - limpar vídeo anterior e permitir upload
            if (videoUrlRef.current && videoUrlRef.current.startsWith('blob:')) {
              URL.revokeObjectURL(videoUrlRef.current);
              videoUrlRef.current = null;
            }
            setVideoUrl(null);
            setVideoFile(null);
          }
        } else {
          setScreenFromAPI(null);
          setVideoUrl(null);
        }
      } catch (error) {
        console.error('Erro ao carregar tela:', error);
        setScreenFromAPI(null);
        setVideoUrl(null);
      } finally {
        setLoadingScreen(false);
      }
    };

    loadScreenFromAPI();
    
    // Cleanup: revogar blob URL quando componente desmontar ou tela mudar
    return () => {
      if (videoUrlRef.current && videoUrlRef.current.startsWith('blob:')) {
        URL.revokeObjectURL(videoUrlRef.current);
        videoUrlRef.current = null;
      }
    };
  }, [selectedScreenId, screens]);

  const selectedScreen = screenFromAPI || (selectedScreenId
    ? screens.find(s => s.id === selectedScreenId) || null
    : null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validar tipo de arquivo
    if (!file.type.startsWith('video/') && !file.name.toLowerCase().endsWith('.mp4')) {
      setSaveMessage({ type: 'error', text: 'Por favor, selecione um arquivo de vídeo .mp4 válido' });
      return;
    }

    // Validar tamanho (limite de 100MB)
    const maxSize = 100 * 1024 * 1024; // 100MB
    if (file.size > maxSize) {
      setSaveMessage({ type: 'error', text: 'O arquivo é muito grande. Tamanho máximo: 100MB' });
      return;
    }

    setVideoFile(file);
    setSaveMessage(null);

    // Limpar blob URL anterior se existir
    if (videoUrlRef.current && videoUrlRef.current.startsWith('blob:')) {
      URL.revokeObjectURL(videoUrlRef.current);
    }

    // Criar blob URL para preview local
    const blobUrl = URL.createObjectURL(file);
    videoUrlRef.current = blobUrl;
    setVideoUrl(blobUrl);
  };

  const handleRemoveVideo = () => {
    // Limpar blob URL se existir
    if (videoUrlRef.current && videoUrlRef.current.startsWith('blob:')) {
      URL.revokeObjectURL(videoUrlRef.current);
      videoUrlRef.current = null;
    }
    setVideoFile(null);
    setVideoUrl(null);
    setSaveMessage(null);
  };

  const handleSaveToAPI = async () => {
    if (!selectedScreen) {
      setSaveMessage({ type: 'error', text: 'Nenhuma tela selecionada' });
      return;
    }

    if (!selectedScreen.id || selectedScreen.id <= 0) {
      setSaveMessage({ type: 'error', text: 'A tela precisa ser salva na API primeiro antes de salvar o vídeo' });
      return;
    }

    if (!videoFile) {
      setSaveMessage({ type: 'error', text: 'Nenhum vídeo para salvar' });
      return;
    }

    setSaving(true);
    setSaveMessage(null);

    try {
      // Fazer upload do vídeo usando o endpoint dedicado
      await bannerService.uploadVideo(selectedScreen.id, videoFile);
      
      // Salvar preferência de loop nas configurações (se necessário)
      // Buscar configurações atuais da API
      const telasAPI = await bannerService.listarTelasOtimizado(selectedScreen.id);
      const telaAtual = telasAPI.find(t => t.id === selectedScreen.id);
      
      if (telaAtual) {
        const parsed = parseConfigForPatch(telaAtual.configuracoes);

        const command: AlterarTelaBannerCommand = {
          id: selectedScreen.id,
          nome: selectedScreen.name,
          tipo: LAYOUT_TO_TIPO[selectedScreen.layoutId || 'video'],
        };

        if (parsed.ok) {
          parsed.config.loop = loop;
          command.configuracoes = JSON.stringify(parsed.config);
        } else if (telaAtual.configuracoes) {
          console.warn(
            '[VideoUploadPage] JSON de configuracoes ilegível; loop não será gravado para não apagar o JSON existente.'
          );
        } else {
          command.configuracoes = JSON.stringify({ loop });
        }

        await bannerService.atualizarTela(command);
      }
      
      setSaveMessage({ type: 'success', text: 'Vídeo salvo com sucesso!' });
      
      // Recarregar vídeo do servidor após upload
      try {
        const videoBlob = await bannerService.downloadVideo(selectedScreen.id);
        if (videoBlob && videoBlob.size > 0) {
          // Limpar URL anterior se existir
          if (videoUrlRef.current && videoUrlRef.current.startsWith('blob:')) {
            URL.revokeObjectURL(videoUrlRef.current);
          }
          const blobUrl = URL.createObjectURL(videoBlob);
          videoUrlRef.current = blobUrl;
          setVideoUrl(blobUrl);
        }
      } catch (error) {
        console.error('Erro ao recarregar vídeo após upload:', error);
      }
      
      // Limpar mensagem após 3 segundos
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (error) {
      setSaveMessage({ 
        type: 'error', 
        text: error instanceof Error ? error.message : 'Erro ao salvar vídeo na API' 
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-3 sm:p-4 lg:p-6">
      <div className="flex items-center mb-4 sm:mb-6">
        <button
          onClick={() => window.history.back()}
          className="mr-2 sm:mr-4 p-1.5 sm:p-2 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft size={18} className="sm:w-[20px] sm:h-[20px]" />
        </button>
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Upload de Vídeo</h2>
          <p className="text-gray-600 text-sm sm:text-base">Faça upload de vídeos para telas do tipo vídeo</p>
        </div>
      </div>

      {/* Save Message */}
      {saveMessage && (
        <div className={`mb-3 sm:mb-4 p-3 sm:p-4 rounded-lg text-sm sm:text-base ${
          saveMessage.type === 'success' 
            ? 'bg-green-50 text-green-700 border border-green-200' 
            : 'bg-red-50 text-red-700 border border-red-200'
        }`}>
          <p className="font-medium">{saveMessage.text}</p>
        </div>
      )}

      {/* Error State */}
      {errorTelasAPI && (
        <div className="mb-3 sm:mb-4 p-3 sm:p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-700 text-sm sm:text-base">Erro ao carregar telas: {errorTelasAPI}</p>
        </div>
      )}

      {loadingScreen && (
        <div className="bg-blue-50 text-blue-700 p-4 sm:p-6 rounded-lg">
          <p className="text-center text-base sm:text-lg">Carregando tela...</p>
        </div>
      )}

      {/* Screen Selection */}
      <div className="mb-4 sm:mb-6">
        <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
          Selecione uma Tela de Vídeo
        </label>
        <div className="relative flex items-center">
          <select
            value={selectedScreenId || ''}
            onChange={(e) => setSelectedScreenId(Number(e.target.value))}
            className="w-full p-2 text-sm sm:text-base border border-gray-300 rounded-lg"
          >
            <option value="">Selecione uma tela de vídeo...</option>
            {screens.map(screen => (
              <option key={screen.id} value={screen.id}>
                {screen.name}
              </option>
            ))}
          </select>
          {loadingTelasAPI && (
            <div className="absolute left-full ml-2 sm:ml-4 flex items-center gap-1.5 sm:gap-2">
              <Loader size={16} className="animate-spin text-blue-500 sm:w-[18px] sm:h-[18px]" />
              <p className="text-gray-600 text-xs sm:text-sm whitespace-nowrap">Carregando telas...</p>
            </div>
          )}
        </div>
        {screens.length === 0 && !loadingTelasAPI && (
          <p className="mt-2 text-xs sm:text-sm text-gray-500">
            Nenhuma tela do tipo vídeo encontrada. Crie uma tela do tipo vídeo primeiro.
          </p>
        )}
      </div>

      {selectedScreen && !loadingScreen && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          {/* Coluna Esquerda - Upload */}
          <div>
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 sm:p-6">
              <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-3 sm:mb-4">Upload de Vídeo</h3>
              
              {/* Upload Input */}
              <div className="mb-3 sm:mb-4">
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                  Arquivo de Vídeo (.mp4)
                </label>
                <div className="relative">
                  <input
                    type="file"
                    accept="video/mp4,.mp4"
                    onChange={handleFileChange}
                    className="w-full text-xs sm:text-sm text-gray-500 file:mr-2 sm:file:mr-4 file:py-1.5 sm:file:py-2 file:px-2 sm:file:px-4 file:rounded-lg file:border-0 file:text-xs sm:file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    disabled={saving || uploading}
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Tamanho máximo: 100MB | Formato: .mp4
                </p>
              </div>

              {/* Video Info */}
              {videoFile && (
                <div className="mb-3 sm:mb-4 p-2 sm:p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0 pr-2">
                      <p className="text-xs sm:text-sm font-medium text-gray-700 truncate">{videoFile.name}</p>
                      <p className="text-xs text-gray-500">
                        {(videoFile.size / (1024 * 1024)).toFixed(2)} MB
                      </p>
                    </div>
                    <button
                      onClick={handleRemoveVideo}
                      className="ml-2 p-1 text-gray-500 hover:text-red-600 transition-colors flex-shrink-0"
                      title="Remover vídeo"
                    >
                      <X size={16} className="sm:w-[18px] sm:h-[18px]" />
                    </button>
                  </div>
                </div>
              )}

              {/* Loop Checkbox */}
              <div className="mb-4 sm:mb-6">
                <label className="flex items-center gap-2 text-xs sm:text-sm text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={loop}
                    onChange={(e) => setLoop(e.target.checked)}
                    className="rounded w-4 h-4 sm:w-5 sm:h-5"
                    disabled={saving || !videoUrl}
                  />
                  <span>Reproduzir em loop</span>
                </label>
              </div>

              {/* Save Button */}
              <button
                onClick={handleSaveToAPI}
                disabled={saving || !videoFile || !selectedScreen}
                className="w-full bg-green-500 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-green-600 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
              >
                <Save size={16} className="sm:w-[18px] sm:h-[18px]" />
                <span>{saving ? 'Salvando...' : 'Salvar vídeo'}</span>
              </button>
            </div>
          </div>

          {/* Coluna Direita - Preview */}
          <div className="lg:sticky lg:top-4 h-fit">
            <VideoPreview
              videoUrl={videoUrl}
              loop={loop}
              onLoopChange={setLoop}
            />
          </div>
        </div>
      )}

      {!selectedScreen && !loadingScreen && screens.length > 0 && (
        <div className="bg-gray-50 p-4 sm:p-6 rounded-lg border border-gray-200">
          <p className="text-center text-gray-500 text-sm sm:text-base">
            Selecione uma tela de vídeo para fazer upload
          </p>
        </div>
      )}
    </div>
  );
};

export default VideoUploadPage;

